import React, { useState, useEffect, useCallback } from 'react';
import './Analytics.css';
import { BarChart3, TrendingUp, Award, Target, Bird, CalendarClock, MapPinned, Shapes } from 'lucide-react';
import { analyticsService } from '../../../../services';
import { appConfig } from '../../../config/appConfig';

const Analytics = () => {
    const [timeFrame, setTimeFrame] = useState('month'); // 'week', 'month', 'year'
    const [analyticsData, setAnalyticsData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const hasAnyAnalyticsData = Boolean(
        analyticsData &&
        (
            analyticsData.rifleEventsCount > 0 ||
            analyticsData.gameDaysCount > 0 ||
            analyticsData.clayEventsCount > 0 ||
            analyticsData.totalRoundsShot > 0
        )
    );

    const fetchAnalytics = useCallback(async () => {
        try {
            setIsLoading(true);
            const data = await analyticsService.getAnalytics(timeFrame);
            setAnalyticsData(data);
        } catch (err) {
            console.error('Error fetching analytics:', err);
            setError('Failed to load analytics. Please try again.');
        } finally {
            setIsLoading(false);
        }
    }, [timeFrame]);

    useEffect(() => {
        fetchAnalytics();
    }, [fetchAnalytics]);

    const clayVariantBreakdown = analyticsData?.claySummary?.variantBreakdown || [];
    const clayStandBreakdown = analyticsData?.claySummary?.standBreakdown || [];
    const clayRecentSessions = analyticsData?.claySummary?.recentSessions || [];

    return (
        <div className="analytics-page">
            {/* Header */}
            <div className="analytics-header">
                <div className="analytics-header-content">
                    <div className="analytics-header-icon">
                        <BarChart3 size={28} />
                    </div>
                    <div className="analytics-header-text">
                        <h1>Analytics Dashboard</h1>
                        <p>{appConfig.messaging.analyticsTagline}</p>
                    </div>
                </div>
            </div>

            {/* Error Message */}
            {error && <div className="analytics-error-message">{error}</div>}

            {/* Time Frame Selector */}
            <div className="analytics-controls">
                <div className="time-frame-selector">
                    <button
                        className={`time-frame-btn ${timeFrame === 'week' ? 'active' : ''}`}
                        onClick={() => setTimeFrame('week')}
                    >
                        This Week
                    </button>
                    <button
                        className={`time-frame-btn ${timeFrame === 'month' ? 'active' : ''}`}
                        onClick={() => setTimeFrame('month')}
                    >
                        This Month
                    </button>
                    <button
                        className={`time-frame-btn ${timeFrame === 'year' ? 'active' : ''}`}
                        onClick={() => setTimeFrame('year')}
                    >
                        This Year
                    </button>
                </div>
            </div>

            {/* Loading State */}
            {isLoading && (
                <div className="analytics-loading">
                    <div className="analytics-spinner"></div>
                    <p>Loading analytics...</p>
                </div>
            )}

            {/* Analytics Content */}
            {!isLoading && analyticsData && (
                <div className="analytics-content">
                    {/* Key Stats */}
                    <div className="analytics-stats-grid">
                        <div className="analytics-stat-card">
                            <div className="analytics-stat-icon rifle">
                                <Target size={24} />
                            </div>
                            <div className="analytics-stat-content">
                                <h3>Rifle Events</h3>
                                <p className="analytics-stat-value">{analyticsData.rifleEventsCount || 0}</p>
                                <span className="analytics-stat-label">Precision shoots tracked</span>
                            </div>
                        </div>

                        <div className="analytics-stat-card">
                            <div className="analytics-stat-icon game">
                                <Bird size={24} />
                            </div>
                            <div className="analytics-stat-content">
                                <h3>Game Days</h3>
                                <p className="analytics-stat-value">{analyticsData.gameDaysCount || 0}</p>
                                <span className="analytics-stat-label">Days in the field</span>
                            </div>
                        </div>

                        <div className="analytics-stat-card">
                            <div className="analytics-stat-icon clay">
                                <Award size={24} />
                            </div>
                            <div className="analytics-stat-content">
                                <h3>Clay Sheets</h3>
                                <p className="analytics-stat-value">{analyticsData.clayEventsCount || 0}</p>
                                <span className="analytics-stat-label">Score sheets tracked</span>
                            </div>
                        </div>

                        <div className="analytics-stat-card">
                            <div className="analytics-stat-icon trend">
                                <TrendingUp size={24} />
                            </div>
                            <div className="analytics-stat-content">
                                <h3>Total Rounds</h3>
                                <p className="analytics-stat-value">{analyticsData.totalRoundsShot || 0}</p>
                                <span className="analytics-stat-label">Cartridges fired</span>
                            </div>
                        </div>
                    </div>

                    {/* Rifle Stats */}
                    {analyticsData.rifleSummary && (
                        <div className="analytics-section">
                            <h2>Rifle Performance</h2>
                            <div className="analytics-detail-grid">
                                <div className="analytics-detail-card">
                                    <h4>Average Score</h4>
                                    <p className="analytics-detail-value">
                                        {analyticsData.rifleSummary.averageScore?.toFixed(1) || '0'}
                                    </p>
                                    <span>across all events</span>
                                </div>
                                <div className="analytics-detail-card">
                                    <h4>Best Score</h4>
                                    <p className="analytics-detail-value">
                                        {analyticsData.rifleSummary.bestScore || '0'}
                                    </p>
                                    <span>highest recorded</span>
                                </div>
                                <div className="analytics-detail-card">
                                    <h4>Accuracy Rate</h4>
                                    <p className="analytics-detail-value">
                                        {analyticsData.rifleSummary.accuracyRate?.toFixed(1) || '0'}%
                                    </p>
                                    <span>hit percentage</span>
                                </div>
                                <div className="analytics-detail-card">
                                    <h4>Events Logged</h4>
                                    <p className="analytics-detail-value">
                                        {analyticsData.rifleSummary.eventsCount || '0'}
                                    </p>
                                    <span>total shoots</span>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Game Stats */}
                    {analyticsData.gameSummary && (
                        <div className="analytics-section">
                            <h2>Game Shooting Performance</h2>
                            <div className="analytics-detail-grid">
                                <div className="analytics-detail-card">
                                    <h4>Total Bag</h4>
                                    <p className="analytics-detail-value">
                                        {analyticsData.gameSummary.totalBag || '0'}
                                    </p>
                                    <span>birds shot</span>
                                </div>
                                <div className="analytics-detail-card">
                                    <h4>Days Out</h4>
                                    <p className="analytics-detail-value">
                                        {analyticsData.gameSummary.daysOut || '0'}
                                    </p>
                                    <span>in the field</span>
                                </div>
                                <div className="analytics-detail-card">
                                    <h4>Avg Cartridges/Bird</h4>
                                    <p className="analytics-detail-value">
                                        {analyticsData.gameSummary.avgCartridgeRatio?.toFixed(2) || '0'}
                                    </p>
                                    <span>efficiency ratio</span>
                                </div>
                                <div className="analytics-detail-card">
                                    <h4>Best Day</h4>
                                    <p className="analytics-detail-value">
                                        {analyticsData.gameSummary.bestDayBag || '0'}
                                    </p>
                                    <span>highest bag</span>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Clay Stats */}
                    {analyticsData.claySummary && (
                        <div className="analytics-section">
                            <h2>Clay Shooting Performance</h2>
                            <div className="analytics-detail-grid">
                                <div className="analytics-detail-card">
                                    <h4>Total Targets</h4>
                                    <p className="analytics-detail-value">
                                        {analyticsData.claySummary.totalTargets || '0'}
                                    </p>
                                    <span>shot</span>
                                </div>
                                <div className="analytics-detail-card">
                                    <h4>Break Rate</h4>
                                    <p className="analytics-detail-value">
                                        {analyticsData.claySummary.breakRate?.toFixed(1) || '0'}%
                                    </p>
                                    <span>average</span>
                                </div>
                                <div className="analytics-detail-card">
                                    <h4>Best Score</h4>
                                    <p className="analytics-detail-value">
                                        {analyticsData.claySummary.bestScore || '0'}
                                    </p>
                                    <span>personal best</span>
                                </div>
                                <div className="analytics-detail-card">
                                    <h4>Score Sheets</h4>
                                    <p className="analytics-detail-value">
                                        {analyticsData.claySummary.eventsCount || '0'}
                                    </p>
                                    <span>tracked this period</span>
                                </div>
                            </div>
                            <div className="analytics-clay-breakdown">
                                <div className="analytics-clay-breakdown-card">
                                    <h4>Competition Scoring</h4>
                                    <p className="analytics-clay-breakdown-value">
                                        {analyticsData.claySummary.competition?.totalTargets || 0}
                                    </p>
                                    <span>
                                        {analyticsData.claySummary.competition?.eventsCount || 0} sheet(s) tracked · {(analyticsData.claySummary.competition?.breakRate || 0).toFixed(1)}% break rate
                                    </span>
                                </div>
                                <div className="analytics-clay-breakdown-card">
                                    <h4>Fun Shoots</h4>
                                    <p className="analytics-clay-breakdown-value">
                                        {analyticsData.claySummary.funShoot?.totalTargets || 0}
                                    </p>
                                    <span>
                                        {analyticsData.claySummary.funShoot?.eventsCount || 0} sheet(s) tracked · {(analyticsData.claySummary.funShoot?.breakRate || 0).toFixed(1)}% break rate
                                    </span>
                                </div>
                            </div>
                            {(clayVariantBreakdown.length > 0 || clayStandBreakdown.length > 0 || clayRecentSessions.length > 0) && (
                                <div className="analytics-clay-insights">
                                    {clayVariantBreakdown.length > 0 && (
                                        <div className="analytics-clay-panel">
                                            <div className="analytics-clay-panel-header">
                                                <h3><Shapes size={18} /> Variant Breakdown</h3>
                                                <p>See which formats you are spending time in and how they are going.</p>
                                            </div>
                                            <div className="analytics-insight-list">
                                                {clayVariantBreakdown.map((variant) => (
                                                    <div key={variant.label} className="analytics-insight-row">
                                                        <div>
                                                            <strong>{variant.label}</strong>
                                                            <small>{variant.sessions} session{variant.sessions === 1 ? '' : 's'}</small>
                                                        </div>
                                                        <div className="analytics-insight-stats">
                                                            <strong>{variant.hits}/{variant.totalTargets}</strong>
                                                            <span>{variant.breakRate.toFixed(1)}%</span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {clayStandBreakdown.length > 0 && (
                                        <div className="analytics-clay-panel">
                                            <div className="analytics-clay-panel-header">
                                                <h3><MapPinned size={18} /> Stand Breakdown</h3>
                                                <p>Use this to spot stands that need attention the next time you go back.</p>
                                            </div>
                                            <div className="analytics-insight-list">
                                                {clayStandBreakdown.map((stand) => (
                                                    <div key={stand.label} className="analytics-insight-row">
                                                        <div>
                                                            <strong>{stand.label}</strong>
                                                            <small>{stand.sessions} visit{stand.sessions === 1 ? '' : 's'}</small>
                                                        </div>
                                                        <div className="analytics-insight-stats">
                                                            <strong>{stand.hits}/{stand.totalTargets}</strong>
                                                            <span>{stand.breakRate.toFixed(1)}%</span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {clayRecentSessions.length > 0 && (
                                        <div className="analytics-clay-panel analytics-clay-panel-full">
                                            <div className="analytics-clay-panel-header">
                                                <h3><CalendarClock size={18} /> Recent Clay Sessions</h3>
                                                <p>Quick look back at what you shot, where you shot it, and what score type it was.</p>
                                            </div>
                                            <div className="analytics-session-list">
                                                {clayRecentSessions.map((session) => (
                                                    <div key={`${session.scoreSheetId}-${session.eventId}`} className="analytics-session-card">
                                                        <div>
                                                            <strong>{session.eventName || 'Clay session'}</strong>
                                                            <p>{session.clubName || 'Venue not set'}</p>
                                                        </div>
                                                        <div className="analytics-session-meta">
                                                            <span>{session.scoreType}</span>
                                                            <span>{session.variant}</span>
                                                        </div>
                                                        <div className="analytics-session-stats">
                                                            <strong>{session.hits}/{session.shots}</strong>
                                                            <span>{session.breakRate.toFixed(1)}%</span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Equipment Usage */}
                    {analyticsData.topEquipment && analyticsData.topEquipment.length > 0 && (
                        <div className="analytics-section">
                            <h2>Equipment Usage</h2>
                            <div className="analytics-equipment-list">
                                {analyticsData.topEquipment.map((equipment, index) => (
                                    <div key={index} className="analytics-equipment-item">
                                        <div className="equipment-rank">{index + 1}</div>
                                        <div className="equipment-info">
                                            <h4>{equipment.name}</h4>
                                            <p>{equipment.type}</p>
                                        </div>
                                        <div className="equipment-stats">
                                            <span className="equipment-stat">
                                                <strong>{equipment.usageCount ?? 0}</strong> tracked actions
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Empty State */}
            {!isLoading && !hasAnyAnalyticsData && (
                <div className="analytics-empty">
                    <BarChart3 size={48} />
                    <h3>No Data Yet</h3>
                    <p>{appConfig.messaging.analyticsEmpty}</p>
                </div>
            )}
        </div>
    );
};

export default Analytics;
